// frontend/src/utils/instructionMode.ts

export type InstructionMode = 'precise' | 'creative';

export interface ModeDetectionResult {
  mode: InstructionMode;
  summary: string; // shown in interpretation preview
}

// ── Precise mode signals ──────────────────────────────────────────────────────

const MANIPULATION_VERBS = [
  'replace', 'swap', 'change', 'update', 'modify', 'move', 'shift',
  'reposition', 'resize', 'add', 'put', 'place', 'insert', 'hang', 'mount',
  'remove', 'delete', 'take out', 'get rid of', 'eliminate', 'hide', 'cover', 'clear',
];

const OBJECT_NOUNS = [
  'sofa', 'couch', 'chair', 'armchair', 'recliner', 'bench', 'stool',
  'ottoman', 'pouf', 'pouffe', 'table', 'desk', 'shelf', 'shelves',
  'cabinet', 'wardrobe', 'dresser', 'sideboard', 'console', 'bed',
  'headboard', 'nightstand', 'lamp', 'light', 'chandelier', 'pendant',
  'sconce', 'fan', 'rug', 'carpet', 'curtain', 'blind', 'cushion',
  'pillow', 'throw', 'blanket', 'plant', 'pot', 'vase', 'art',
  'painting', 'mirror', 'clock', 'frame', 'seater', 'bean bag', 'beanbag',
  'floor seater', 'mattress', 'tv', 'television', 'screen', 'fireplace',
  'bookcase', 'rack', 'hanger', 'door', 'window', 'wall', 'floor',
  'ceiling', 'ceiling fan', 'coffee table', 'side table', 'end table',
  'dining table', 'dining chair', 'bar stool', 'bar cart', 'shoe rack',
  'coat rack', 'curtain rod', 'blinds', 'shades', 'wallpaper', 'tiles',
];

const LOCATION_WORDS = [
  'region', 'left', 'right', 'corner', 'wall', 'floor', 'ceiling',
  'window', 'door', 'background', 'foreground', 'centre', 'center',
  'middle', 'top', 'bottom', 'above', 'below', 'beside', 'behind',
  'front', 'back', 'near', 'next to', 'opposite',
];

// ── Creative mode signals ─────────────────────────────────────────────────────

const STYLE_WORDS = [
  'traditional', 'modern', 'contemporary', 'minimal', 'minimalist', 'rustic',
  'industrial', 'scandinavian', 'japandi', 'bohemian', 'boho', 'eclectic',
  'coastal', 'farmhouse', 'mid-century', 'art deco', 'victorian', 'colonial',
  'tropical', 'mediterranean', 'indian', 'chinese', 'japanese', 'korean',
  'nordic', 'french', 'italian', 'arabic', 'moroccan', 'balinese',
  'wabi-sabi', 'zen', 'retro', 'vintage', 'classic', 'luxurious', 'luxury',
  'glam', 'maximalist', 'transitional', 'preppy', 'hollywood regency',
];

const MOOD_WORDS = [
  'cozy', 'cosy', 'warm', 'bright', 'airy', 'dark', 'moody', 'dramatic',
  'elegant', 'luxe', 'vibrant', 'calm', 'serene', 'peaceful', 'bold',
  'playful', 'natural', 'organic', 'earthy', 'fresh', 'clean', 'simple',
  'homely', 'inviting', 'welcoming', 'cheerful', 'sophisticated', 'timeless',
];

const CREATIVE_PHRASES = [
  'make it look', 'give it a', 'give the room', 'transform', 'restyle',
  'redesign', 'redecorate', 'makeover', 'renovate', 'new look', 'aesthetic',
  'vibe', 'feel', 'color palette', 'colour palette', 'colour scheme',
  'color scheme', 'warm tones', 'cool tones', 'earthy tones',
  'neutral palette', 'muted tones', 'atmosphere', 'ambiance', 'ambience',
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function containsAny(text: string, words: string[]): string | null {
  for (const w of words) {
    const escaped = w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    if (new RegExp(`\\b${escaped}\\b`).test(text)) return w;
  }
  return null;
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Classifies a free-form instruction as 'precise' or 'creative'.
 * If chipMode is provided it wins outright — no keyword analysis runs.
 */
export function detectMode(
  instruction: string,
  chipMode?: InstructionMode,
): ModeDetectionResult {
  if (chipMode) {
    const summary =
      chipMode === 'precise'
        ? `Precise edit: ${instruction.replace(/\[___\]/g, '…').trim() || 'custom instruction'}`
        : `Creative restyle: ${instruction.replace(/\[___\]/g, '…').trim() || 'custom style'}`;
    return { mode: chipMode, summary };
  }

  const lower = instruction.toLowerCase().trim();
  if (!lower) return { mode: 'precise', summary: '' };

  const hasVerb   = containsAny(lower, MANIPULATION_VERBS);
  const hasObject = containsAny(lower, OBJECT_NOUNS);
  const hasLoc    = containsAny(lower, LOCATION_WORDS);
  const hasStyle  = containsAny(lower, STYLE_WORDS);
  const hasMood   = containsAny(lower, MOOD_WORDS);
  const hasPhrase = containsAny(lower, CREATIVE_PHRASES);

  const isPrecise  = hasVerb !== null && (hasObject !== null || hasLoc !== null);
  const isCreative = (hasStyle !== null || hasMood !== null || hasPhrase !== null);

  // Precise wins on conflict; precise is the default when neither matches
  if (isPrecise || !isCreative) {
    const verbWord   = hasVerb   ?? 'edit';
    const objectWord = hasObject ?? hasLoc ?? 'item';
    return {
      mode: 'precise',
      summary: `Precise edit: ${verbWord} ${objectWord}`,
    };
  }

  const styleWord = hasStyle ?? hasMood ?? hasPhrase ?? 'custom';
  return {
    mode: 'creative',
    summary: `Creative restyle: ${styleWord} aesthetic`,
  };
}
